import { API_PREFIX, DEFAULT_TIMEZONE } from '@adco-rooms/shared';

/** Authentication strategy for the SPA. */
export type AuthMode = 'dev' | 'entra';

interface RawEnv {
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_AUTH_MODE?: string;
  readonly VITE_AZURE_CLIENT_ID?: string;
  readonly VITE_AZURE_TENANT_ID?: string;
  readonly VITE_AZURE_API_SCOPE?: string;
  readonly VITE_DEFAULT_TIMEZONE?: string;
  readonly VITE_REALTIME_ENABLED?: string;
}

const raw = import.meta.env as unknown as RawEnv;

const readString = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const readBoolean = (value: string | undefined, fallback: boolean): boolean => {
  const trimmed = readString(value);
  if (trimmed === null) return fallback;
  return trimmed === 'true' || trimmed === '1';
};

/** Strips a trailing slash so we can concatenate paths safely. */
const stripTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

/**
 * Guarantees the API origin carries a scheme.
 *
 * A value like `api.example.com` is not an origin: prefixed to a path it makes
 * a *relative* URL, so the browser resolves it against the page's own origin
 * and every call lands on the SPA instead of the API. The SPA answers 200 with
 * `index.html`, so nothing throws — the session simply comes back empty and the
 * app reports itself unavailable, with no failed request anywhere to explain
 * why. That is a long hour to spend on a missing `https://`, so a schemeless
 * value is repaired here and reported loudly rather than trusted.
 */
function normalizeOrigin(value: string | null): string | null {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;

  const isLoopback = /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?(\/|$)/i.test(value);
  const repaired = `${isLoopback ? 'http' : 'https'}://${value}`;
  // eslint-disable-next-line no-console -- a misconfiguration the operator must see.
  console.warn(
    `VITE_API_BASE_URL no incluye el esquema ("${value}"); se asume "${repaired}". Corrige la variable.`,
  );
  return repaired;
}

const apiOrigin = normalizeOrigin(readString(raw.VITE_API_BASE_URL));
const authModeRaw = readString(raw.VITE_AUTH_MODE);
const authMode: AuthMode = authModeRaw === 'entra' ? 'entra' : 'dev';
const azureClientId = readString(raw.VITE_AZURE_CLIENT_ID);
const azureTenantId = readString(raw.VITE_AZURE_TENANT_ID);

export const env = {
  /**
   * Absolute base URL of the versioned API.
   * Falls back to the relative `/api/v1` path, which lets the Vite dev proxy
   * (and a same-origin production deployment) handle the request.
   */
  apiBaseUrl: apiOrigin ? `${stripTrailingSlash(apiOrigin)}${API_PREFIX}` : API_PREFIX,

  /** Origin used by socket.io; empty string means "same origin". */
  socketUrl: apiOrigin ? stripTrailingSlash(apiOrigin) : '',

  authMode,
  isDevAuth: authMode === 'dev',

  azureClientId,
  azureTenantId,
  azureAuthority: azureTenantId ? `https://login.microsoftonline.com/${azureTenantId}` : null,

  /**
   * Scope requested for our own API. Defaults to the conventional
   * `api://<clientId>/access_as_user` exposed by the Entra app registration.
   */
  azureApiScope:
    readString(raw.VITE_AZURE_API_SCOPE) ??
    (azureClientId ? `api://${azureClientId}/access_as_user` : null),

  defaultTimeZone: readString(raw.VITE_DEFAULT_TIMEZONE) ?? DEFAULT_TIMEZONE,

  realtimeEnabled: readBoolean(raw.VITE_REALTIME_ENABLED, true),

  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
} as const;

/** True when Entra sign-in outside Teams can actually be attempted. */
export const canUseMsal = (): boolean =>
  env.authMode === 'entra' && Boolean(env.azureClientId && env.azureAuthority && env.azureApiScope);
